import React from 'react';
import { ProjectUnlockData } from '@/types/project';
import { formatCompactNumber, formatCompactCurrency } from '@/utils';
import { Progress } from '@/components/ui/progress';
import UnlockPieChart from './UnlockPieChart';
import UnlockTimelineChart from './UnlockTimelineChart';
import { 
  Calendar, 
  DollarSign, 
  Lock, 
  Unlock,
  FileText,
  Bell
} from 'lucide-react';

interface ProjectUnlocksProps {
  unlockData: ProjectUnlockData | null;
}

const ProjectUnlocks: React.FC<ProjectUnlocksProps> = ({ unlockData }) => {
  const [showNotifications, setShowNotifications] = React.useState(false);
  
  if (!unlockData) {
    return (
      <div className="text-center py-12">
        <p className="font-['Plus_Jakarta_Sans'] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
          No unlock data available
        </p>
      </div>
    );
  }

  // Calculate unlock progress based on time elapsed in vesting period
  const calculateUnlockProgress = (allocation: { tge_unlock_percent?: number }) => {
    const now = Date.now();
    const vestingStart = new Date(unlockData.vesting_start_date).getTime();
    const vestingEnd = new Date(unlockData.vesting_end_date).getTime();
    const tgePercent = allocation.tge_unlock_percent || 0;
    
    if (now < vestingStart) return Math.min(tgePercent, 100);
    if (now >= vestingEnd) return 100;
    
    const totalVestingTime = vestingEnd - vestingStart;
    const elapsedTime = now - vestingStart;
    const timeBasedProgress = (elapsedTime / totalVestingTime) * (100 - tgePercent);
    
    return Math.min(Math.max(tgePercent + timeBasedProgress, 0), 100);
  };

  // Calculate timeline progress
  const calculateTimelineProgress = () => {
    const now = Date.now();
    const vestingStart = new Date(unlockData.vesting_start_date).getTime();
    const vestingEnd = new Date(unlockData.vesting_end_date).getTime();
    
    if (now < vestingStart) return 0;
    if (now >= vestingEnd) return 100;
    
    return Math.min(Math.max(((now - vestingStart) / (vestingEnd - vestingStart)) * 100, 0), 100);
  };

  const timelineProgress = calculateTimelineProgress();

  // Format date to DD/MM/YYYY
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB');
  };

  // Calculate unlocked and locked percentages
  const totalSupply = unlockData.total_supply || 1;
  const unlockedPercent = ((unlockData.total_unlocked || 0) / totalSupply * 100).toFixed(1);
  const lockedPercent = ((unlockData.total_locked || 0) / totalSupply * 100).toFixed(1);

  return (
    <div className="space-y-6">
      {/* Vesting Timeline Bar */}
      <div className="bg-white dark:bg-[#121212] h-[140px] relative px-8 py-6">
        {/* Dates */}
        <div className="flex justify-between mb-4">
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
            {formatDate(unlockData.vesting_start_date)}
          </span>
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
            {formatDate(unlockData.vesting_end_date)}
          </span>
        </div>
        
        {/* Progress Bar */}
        <div className="relative h-[11px] bg-[rgba(45,41,35,0.12)] dark:bg-[rgba(253,253,253,0.12)] rounded-[8px]">
          <div
            className="absolute h-[11px] bg-[#2d2923] dark:bg-[#fdfdfd] rounded-[8px] transition-all duration-300"
            style={{ width: `${timelineProgress}%` }}
          />
          {/* Current marker */}
          <div
            className="absolute top-0 h-[11px] w-[6px] bg-[#2d2923] dark:bg-[#fdfdfd] rounded-[8px]"
            style={{ left: `calc(${timelineProgress}% - 3px)` }}
          />
        </div>
        
        {/* Labels */}
        <div className="flex justify-between mt-3">
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            Vesting Start
          </span>
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            Current
          </span>
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            Vesting End
          </span>
        </div>
      </div>

      {/* Token Distribution & Unlock Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Token Distribution */}
        <div className="bg-[rgba(255,255,255,0.72)] dark:bg-[rgba(28,28,28,0.72)] border border-white dark:border-[rgba(255,255,255,0.1)] p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-['Plus_Jakarta_Sans'] font-semibold text-[20px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]">
              Token Distribution
            </h2>
            <div className="relative">
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="Notification Settings"
              >
                <Bell size={20} className={showNotifications ? "text-primary-500" : "text-[rgba(45,41,35,0.64)]"} />
              </button>
              
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-900 border border-border shadow-xl z-50 p-4 rounded-lg animate-in fade-in zoom-in duration-200">
                  <p className="text-xs font-semibold uppercase opacity-50 mb-3">Notify me</p>
                  <div className="space-y-2">
                    {['On Unlock', 'Weekly', 'Monthly'].map(option => (
                      <button 
                        key={option}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-primary/10 rounded transition-colors"
                        onClick={() => setShowNotifications(false)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex gap-8">
            {/* Donut Chart */}
            <div className="relative w-[298px] h-[298px]">
              <UnlockPieChart unlockData={unlockData} />
            </div>
            
            {/* Legend Cards */}
            <div className="flex flex-col gap-4 flex-1">
              {/* Unlocked Card */}
              <div className="bg-white dark:bg-[#1a1a1a] border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-[19px] h-[8px] bg-[#c4e804]" />
                  <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
                    Unlocked
                  </span>
                </div>
                <p className="font-['Plus_Jakarta_Sans'] font-bold text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]">
                  {formatCompactNumber(unlockData.total_unlocked)}
                </p>
                <p className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  {unlockedPercent}% of supply
                </p>
              </div>
              
              {/* Locked Card */}
              <div className="bg-white dark:bg-[#1a1a1a] border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-[19px] h-[8px] bg-[#6b6969]" />
                  <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
                    Locked
                  </span>
                </div>
                <p className="font-['Plus_Jakarta_Sans'] font-bold text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]">
                  {formatCompactNumber(unlockData.total_locked)}
                </p>
                <p className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  {lockedPercent}% of supply
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Unlock Timeline */}
        <div className="bg-[rgba(255,255,255,0.72)] dark:bg-[rgba(28,28,28,0.72)] border border-white dark:border-[rgba(255,255,255,0.1)] p-8">
          <h2 className="font-['Plus_Jakarta_Sans'] font-semibold text-[20px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] mb-6">
            Unlock Timeline
          </h2>
          
          <div className="flex gap-6">
            {/* Chart */}
            <div className="flex-1 h-[280px]">
              <UnlockTimelineChart unlockData={unlockData} />
            </div>
            
            {/* Date Cards */}
            <div className="flex flex-col gap-3 w-[216px]">
              <div className="bg-white dark:bg-[#1a1a1a] border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] px-5 py-4">
                <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  Vesting Start
                </span>
                <p className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] mt-3">
                  {formatDate(unlockData.vesting_start_date)}
                </p>
              </div>
              <div className="bg-white dark:bg-[#1a1a1a] border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] px-5 py-4">
                <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  Vesting End
                </span>
                <p className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] mt-3">
                  {formatDate(unlockData.vesting_end_date)}
                </p>
              </div>
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex justify-center gap-8 mt-6">
            <div className="flex items-center gap-3">
              <div className="w-[19px] h-[8px] bg-[#a9c804]" />
              <span className="font-['Plus_Jakarta_Sans'] font-medium text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                Unlocked Tokens
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-[19px] h-[8px] bg-[#ee9d1a]" />
              <span className="font-['Plus_Jakarta_Sans'] font-medium text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                Locked Tokens
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Metric Cards - Row 1: Supply cards */}
      <div className="grid grid-cols-2 gap-4">
        {/* Total Supply */}
        <div className="bg-white dark:bg-[#121212] p-4 flex items-center justify-between">
          <div className="flex items-center gap-[6px]">
            <DollarSign size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
              Total Supply
            </span>
          </div>
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
            <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span> {formatCompactNumber(unlockData.total_supply)}
          </span>
        </div>
        
        {/* Circulating Supply */}
        <div className="bg-white dark:bg-[#121212] p-4 flex items-center justify-between">
          <div className="flex items-center gap-[6px]">
            <FileText size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
              Circulating Supply
            </span>
          </div>
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
            <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span> {formatCompactNumber(unlockData.circulating_supply)}
          </span>
        </div>
      </div>

      {/* Metric Cards - Row 2: Locked/Unlocked cards */}
      <div className="grid grid-cols-2 gap-4">
        {/* Total Locked */}
        <div className="bg-white dark:bg-[#121212] p-4 flex items-center justify-between">
          <div className="flex items-center gap-[6px]">
            <Lock size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
              Total Locked
            </span>
          </div>
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
            <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span> {formatCompactNumber(unlockData.total_locked)}
          </span>
        </div>

        {/* Total Unlocked */}
        <div className="bg-white dark:bg-[#121212] p-4 flex items-center justify-between">
          <div className="flex items-center gap-[6px]">
            <Unlock size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
              Total Unlocked
            </span>
          </div>
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
            <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span> {formatCompactNumber(unlockData.total_unlocked)}
          </span>
        </div>
      </div>

      {/* Metric Cards - Row 3: Date cards */}
      <div className="grid grid-cols-3 gap-4">
        {/* Vesting Start */}
        <div className="bg-[rgba(255,255,255,0.4)] dark:bg-[rgba(28,28,28,0.4)] px-5 py-6 flex flex-col justify-between h-[128px]">
          <div className="flex items-center gap-[6px]">
            <Calendar size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[13px] leading-[18px] text-[rgba(45,41,35,0.8)] dark:text-[rgba(253,253,253,0.8)]">
              Vesting Start
            </span>
          </div>
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
            {formatDate(unlockData.vesting_start_date)}
          </span>
        </div>
        
        {/* Vesting End */}
        <div className="bg-[rgba(255,255,255,0.4)] dark:bg-[rgba(28,28,28,0.4)] px-5 py-6 flex flex-col justify-between h-[128px]">
          <div className="flex items-center gap-[6px]">
            <Calendar size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[13px] leading-[18px] text-[rgba(45,41,35,0.8)] dark:text-[rgba(253,253,253,0.8)]">
              Vesting End
            </span>
          </div>
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
            {formatDate(unlockData.vesting_end_date)}
          </span>
        </div>
        
        {/* Next Unlock */}
        <div className="bg-[rgba(255,255,255,0.4)] dark:bg-[rgba(28,28,28,0.4)] px-5 py-6 flex flex-col justify-between h-[128px]">
          <div className="flex items-center gap-[6px]">
            <Calendar size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[13px] leading-[18px] text-[rgba(45,41,35,0.8)] dark:text-[rgba(253,253,253,0.8)]">
              Next Unlock
            </span>
          </div>
          <div>
            {unlockData.next_unlock_tokens > 0 && (
              <p className="font-['Plus_Jakarta_Sans'] font-normal text-[12px] leading-[16px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">
                {formatCompactNumber(unlockData.next_unlock_tokens)} tokens
              </p>
            )}
            <span className="font-['Plus_Jakarta_Sans'] font-medium text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
              {unlockData.next_unlock_date ? formatDate(unlockData.next_unlock_date) : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Allocations Grid - 2 columns */}
      {unlockData.allocations && unlockData.allocations.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {unlockData.allocations.map((allocation, index) => {
            const unlockProgress = calculateUnlockProgress(allocation);
            
            return (
              <div 
                key={index} 
                className="bg-[rgba(255,255,255,0.44)] dark:bg-[rgba(28,28,28,0.44)] border border-[rgba(45,41,35,0.08)] dark:border-[rgba(253,253,253,0.08)] p-6"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
                      {allocation.name}
                    </h3>
                    <p className="font-['Plus_Jakarta_Sans'] font-normal text-[12px] leading-[16px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">
                      {allocation.unlock_type}
                      {allocation.tge_unlock_percent > 0 && ` • ${allocation.tge_unlock_percent}% TGE`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
                      <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span> {formatCompactCurrency(allocation.tokens)}
                    </p>
                    <p className="font-['Plus_Jakarta_Sans'] font-normal text-[12px] leading-[16px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">
                      {allocation.allocation_of_supply.toFixed(1)}% of supply
                    </p>
                  </div>
                </div>

                {/* Progress */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                      Unlock Progress
                    </span>
                    <span className="font-['Plus_Jakarta_Sans'] font-normal text-[12px] leading-[16px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
                      {unlockProgress.toFixed(1)}% unlocked
                    </span>
                  </div>
                  <Progress value={unlockProgress} className="h-[8px] bg-[rgba(45,41,35,0.12)] dark:bg-[rgba(253,253,253,0.12)]" />
                </div>

                {/* Details */}
                <div className="flex gap-8">
                  {allocation.tge_unlock > 0 && (
                    <div>
                      <p className="font-['Plus_Jakarta_Sans'] font-normal text-[12px] leading-[16px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)] mb-1">
                        TGE Unlock
                      </p>
                      <p className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                        {formatCompactNumber(allocation.tge_unlock)}
                      </p>
                    </div>
                  )}
                  {allocation.next_unlock_date && (
                    <div>
                      <p className="font-['Plus_Jakarta_Sans'] font-normal text-[12px] leading-[16px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)] mb-1">
                        Next Unlock Date
                      </p>
                      <p className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                        {formatDate(allocation.next_unlock_date)}
                      </p>
                    </div>
                  )}
                  {allocation.next_unlock_tokens > 0 && (
                    <div>
                      <p className="font-['Plus_Jakarta_Sans'] font-normal text-[12px] leading-[16px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)] mb-1">
                        Next Unlock Amount
                      </p>
                      <p className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                        {formatCompactNumber(allocation.next_unlock_tokens)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProjectUnlocks; 